import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { toApiError, type StartSupportSessionRequest } from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { SupportStartMismatchError } from '@/shared/auth/support/supportFailure';
import {
  supportStartMismatchProblem,
  supportStartProblem,
  type SupportStartProblem,
} from './supportStartModel';

export interface SupportTarget {
  id: string;
  name: string;
}

/**
 * Beginn einer Support-Sitzung aus der Heimatsitzung. Ohne eingeschaltete 2FA zeigt der Dialog
 * gleich den Weg zur Einrichtung statt eines Formulars, das der Server ohnehin ablehnte.
 */
export function useSupportStart(target: SupportTarget | null, onStarted: () => void) {
  const { startSupport, homeApi, homeUser } = useAuth();
  const [problem, setProblem] = useState<SupportStartProblem | null>(null);
  const twoFactor = useQuery({
    queryKey: ['support-session', 'two-factor', homeUser?.userId],
    queryFn: () => homeApi.accountSecurity.getTwoFactor(),
    enabled: target !== null,
    staleTime: 60_000,
  });
  const start = useMutation({
    mutationFn: (request: StartSupportSessionRequest) => startSupport(request),
    networkMode: 'always',
    onMutate: () => setProblem(null),
    onSuccess: () => {
      if (target) toast.success(`Sie arbeiten jetzt als Support in „${target.name}“.`);
      onStarted();
    },
    onError: async (error) =>
      setProblem(
        error instanceof SupportStartMismatchError
          ? supportStartMismatchProblem(error)
          : supportStartProblem(await toApiError(error)),
      ),
  });
  const { reset: resetStart } = start;
  const reset = useCallback(() => {
    setProblem(null);
    resetStart();
  }, [resetStart]);

  return {
    submit: (values: { code: string }) => {
      if (target) start.mutate({ workspaceId: target.id, ...values });
    },
    pending: start.isPending,
    problem,
    // Scheitert die Vorabprüfung, entscheidet der Server beim Absenden.
    checkingTwoFactor: twoFactor.isLoading,
    twoFactorMissing: twoFactor.data?.enabled === false || problem?.kind === 'two-factor-missing',
    reset,
  };
}
